import React, { useState, useEffect } from "react";
import FormatUrl from "../../../utils/UrlFormatter";
import { getClinicId } from "../../../utils/config";
import PatientNameDisplay from "./PatientNameDisplay";
import DisciplinePicker from "./DisciplinePicker";
import DateSliderBar from "./DateSliderBar";
import PractitionerSlotGrid from "./PractitionerSlotGrid";
import PatientConfirmModal from "./PatientConfirmModal";
import "../styles/PatientAvailabilityTool.css";
import { useTranslation } from "react-i18next";

const PatientAvailabilityTool = ({ user }) => {
  const { t } = useTranslation();
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlotInfo, setSelectedSlotInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [slotData, setSlotData] = useState([]);
  const [isLoadingDisciplines, setIsLoadingDisciplines] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);


  useEffect(() => {
    const fetchDisciplines = async () => {
      setIsLoadingDisciplines(true);
      try {
        const clinicId = getClinicId(user);
        const url = FormatUrl(`/get-clinic-disciplines/?clinic_id=${clinicId}`);
        const res = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        const data = await res.json();

        if (data?.status === "Success" && Array.isArray(data?.disciplines)) {
          const formatted = data.disciplines.map((item) => ({
            id: item.name,
            label: item.name,
            ...item,
          }));
          setDisciplines(formatted);
        } else {
          setDisciplines([]);
        }
      } catch {
        setDisciplines([]);
      } finally {
        setIsLoadingDisciplines(false);
      }
    };

    fetchDisciplines();
  }, [user]);

  useEffect(() => {
    setSelectedSlotInfo(null);
  }, [selectedDate]);

  useEffect(() => {
    const fetchSlotData = async () => {
      if (!selectedDiscipline) return;
  
      setIsLoadingSlots(true);
      try {
        const clinicId = getClinicId(user);
  
        // Define start and end date for 21-day window
        const today = new Date();
        const start = new Date(today);
        start.setDate(start.getDate() + weekOffset * 14);

        const end = new Date(start);
        end.setDate(start.getDate() + 13);

        const startDate = start.toISOString().split("T")[0];
        const endDate = end.toISOString().split("T")[0];

        
        // const startDate = today.toISOString();
        // const endDate = futureDate.toISOString();
        const url = FormatUrl(
          `/get-slot-groupedby/?clinic_id=${clinicId}&discipline=${selectedDiscipline}&start_date=${startDate}&end_date=${endDate}`
        );
  
        const res = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
  
        const data = await res.json();
  
        if (data?.status === "success" && Array.isArray(data?.slots)) {
          setSlotData(data.slots);
        } else {
          setSlotData([]);
        }
      } catch {
        setSlotData([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };
  
    fetchSlotData();
  }, [selectedDiscipline, user, weekOffset]);
  
  const filterSlotDataByDate = (data, selectedDate) => {
    if (!selectedDate) return [];
  
    //const targetDate = new Date(selectedDate).toLocaleDateString("en-CA");
    const targetDate = selectedDate
   
    return data
      .map((practitioner) => {
        const filtered = { ...practitioner };
  
        Object.keys(practitioner).forEach((key) => {
          if (key.startsWith("appointments")) {
            filtered[key] = (practitioner[key] || []).filter((slot) => {
              const slotDate = new Date(slot.start).toLocaleDateString("en-CA");
              //const slotDate = new Date(slot.start).toISOString().split('T')[0];
              //console.log("slotDate -", slotDate,  "targetDate -", targetDate, "slotStart-", slot.start);
              return slotDate === targetDate;
            });
          }
        });
  
        return filtered;
      })
      .filter((p) =>
        Object.keys(p).some(
          (k) => k.startsWith("appointments") && (p[k] || []).length > 0
        )
      );
  };
  

  const resetAllFields = () => {
    setSelectedDiscipline(null);
    setSelectedDate(null);
    setSelectedSlotInfo(null);
    setShowInfoModal(false);
  };

  const handleSlotSelect = (slotDetails) => {
    // if (selectedDiscipline === "Physiotherapy") {
    //   setShowInfoModal(true);
    //   return;
    // }
    setSelectedSlotInfo(slotDetails);
  };

  const handleProceedToBook = () => {
    if (selectedSlotInfo) {
      setShowModal(true);
    }
  };

  return (
    <div className="availability-container">
      <PatientNameDisplay user={user} />

      <DisciplinePicker
        disciplines={disciplines}
        selectedDiscipline={selectedDiscipline}
        onSelect={setSelectedDiscipline}
        onResetDate={() => setSelectedDate(null)}
        onResetSlot={() => setSelectedSlotInfo(null)}
        isLoading={isLoadingDisciplines}
      />

      {selectedDiscipline && (
        <DateSliderBar
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          isLoading={isLoadingSlots}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
        />
      )}


      {selectedDiscipline && selectedDate && (
        <PractitionerSlotGrid
          date={selectedDate}
          slotData={filterSlotDataByDate(slotData, selectedDate)}
          onSlotSelect={handleSlotSelect}
          selectedSlotInfo={selectedSlotInfo}
          isLoading={isLoadingSlots}
          discipline={selectedDiscipline}
        />
      )}

      {/* Massage Therapy Info Block */}
      {selectedDiscipline === "Massage Therapy" && selectedSlotInfo && (
        <div className="slot-info-banner">
          {t("patient_scheduling_new.slot_duration_hint_line1")}
          <br />
          {t("patient_scheduling_new.slot_duration_hint_line2")}
        </div>
      )}

      {/* Proceed Button */}
      {selectedSlotInfo && (
        <div className="booking-footer">
          <button className="proceed-btn" onClick={handleProceedToBook}>
          {t("pat_new_sched.proceed_to_book")}
          </button>
        </div>
      )}

      {/* Booking Modal */}
      {showModal && (
        <PatientConfirmModal
        slotInfo={{ ...selectedSlotInfo, discipline: selectedDiscipline }}
        onClose={() => setShowModal(false)}
        user={user}
        resetAllFields={resetAllFields} 
      />   
      )}

      {/* Info Modal for Physiotherapy */}
      {showInfoModal && (
        <div className="modal-overlay_v3">
          <div className="pb-modal-content">
            <h3>{t("patient_scheduling_new.info_title")}</h3>
            <p>{t("patient_scheduling_new.info_message_line2")}</p>
            <p>
              <strong>{t("patient_scheduling_new.clinic_contact_label")}</strong>
            </p>
            <div className="pb-modal-actions">
              <button onClick={resetAllFields} className="pb-cancel-btn">
                {t("patient_scheduling_new.back_to_booking")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientAvailabilityTool;
